import React, { useState, useRef, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUI } from '../UIContext';
import Login from '../Login';
import PageWrapper from '../components/PageWrapper';
import { useActiveSession } from '../ActiveSessionContext';
import myIcon from '../assets/logo512.png'; // adjust path
import { normalizeKey, normalizeType, keyRoot, normalizeGenre, normalizeTag, normalizeTagsArray } from '../utils/normalize';
import { useUserPrefs } from '../UserPrefsProvider'; // near the top
import AbcPreview from '../components/AbcPreview';
import BorderedBox from '../components/BorderedBox';
import TuneCardBox from '../components/TuneCardBox';
import StyledCheckbox from '../components/StyledCheckbox';
import TagFilterControl from '../components/TagFilterControl'; // adjust if needed
import CustomTagInput from '../components/CustomTagInput';
import { useOnboarding } from '../onboarding';
import dexie from '../offlineDB';
import SessionPromptModal from '../components/SessionPromptModal';
import { getUserProfile, sendBatchToUser, sendTuneToUser } from '../utils/socialFirestore';
import ActionConfirmModal from '../components/ActionConfirmModal';
import ModeSwitcher from '../components/ModeSwitcher';
import { FaQuestionCircle } from 'react-icons/fa';
import AppSelect from '../components/AppSelect';
import TimeFilterPanel from '../components/TimeFilterPanel';
import UndoBanner from '../components/UndoBanner';
import PreviewModePill from '../components/PreviewModePill';
import TheSessionImportModal from '../components/TheSessionImportModal';
import SwipeablePinnableRow from '../components/SwipeablePinnableRow';
import SendExportModal from '../components/SendExportModal';
import ExportModal from '../components/ExportModal';
import QuickAddNoteModal from '../components/QuickAddNoteModal';

const SEARCH_FILTERS_STORAGE_KEY = 'activeSearchFiltersByMode';
const LEGACY_SEARCH_FILTERS_STORAGE_KEY = 'activeSearchFilters';
const SEARCH_UI_STORAGE_KEY = 'searchUiPrefs';

const normalizeModeKey = (mode) => (mode === 'notes' || mode === 'all' ? mode : 'tunes');

const hasFilterValues = (filters) => Boolean(filters && typeof filters === 'object' && Object.keys(filters).length > 0);

const getFiltersForMode = (byMode, mode, lastPureMode) => {
  const targetMode = normalizeModeKey(mode);
  const direct = byMode?.[targetMode];
  if (hasFilterValues(direct)) return direct;
  if (targetMode === 'all') {
    const fallbackMode = normalizeModeKey(lastPureMode || 'tunes');
    const fallback = byMode?.[fallbackMode];
    if (hasFilterValues(fallback)) return fallback;
  }
  return {};
};

const readLocalSearchFilterBundle = () => {
  let byMode = {};
  let lastPureMode = 'tunes';
  let legacy = {};

  try {
    const raw = JSON.parse(localStorage.getItem(SEARCH_FILTERS_STORAGE_KEY) || '{}');
    if (raw && typeof raw === 'object') {
      byMode = raw.byMode && typeof raw.byMode === 'object' ? raw.byMode : {};
      lastPureMode = normalizeModeKey(raw.lastPureMode || 'tunes');
    }
  } catch {}

  try {
    legacy = JSON.parse(localStorage.getItem(LEGACY_SEARCH_FILTERS_STORAGE_KEY) || '{}');
  } catch {}

  return { byMode, lastPureMode, legacy };
};

const readSearchUiPrefs = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = JSON.parse(localStorage.getItem(SEARCH_UI_STORAGE_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
};

const getStoredAdvancedFiltersOpen = (mode) => {
  const prefs = readSearchUiPrefs();
  const byMode = prefs?.advancedFiltersOpenByMode;
  if (!byMode || typeof byMode !== 'object') return null;
  const value = byMode[normalizeModeKey(mode)];
  return typeof value === 'boolean' ? value : null;
};

const storeAdvancedFiltersOpen = (mode, open) => {
  if (typeof window === 'undefined') return;
  const prefs = readSearchUiPrefs();
  const byMode = {
    ...(prefs?.advancedFiltersOpenByMode && typeof prefs.advancedFiltersOpenByMode === 'object'
      ? prefs.advancedFiltersOpenByMode
      : {}),
    [normalizeModeKey(mode)]: Boolean(open),
  };
  localStorage.setItem(
    SEARCH_UI_STORAGE_KEY,
    JSON.stringify({
      ...(prefs && typeof prefs === 'object' ? prefs : {}),
      advancedFiltersOpenByMode: byMode,
    })
  );
};

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const toPlainText = (value = '') =>
  String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();

const getQueryTokens = (rawQuery = '') =>
  Array.from(
    new Set(
      String(rawQuery)
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean)
    )
  );

const getSearchMatches = (tune, rawQuery) => {
  const q = (rawQuery || '').trim().toLowerCase();
  const tokens = getQueryTokens(rawQuery);
  if (!tokens.length) return [];
  const fields = [
    ['Title', tune.title],
    ['Genre', tune.genre],
    ['Type', tune.type],
    ['Key', tune.key],
    ['Composer', tune.composer],
    ['Status', tune.status],
    ['Rating', tune.rating],
    ['Rating Label', tune.ratingLabel],
    ['Event Date', tune.eventDate],
    ['Quick Note', tune.quickNote],
    ['Notes', tune.notes],
    ['Lyrics', tune.lyrics],
    ['ABC', tune.abc],
    ['Source', tune.source],
    ['Learned From', tune.learnedFrom],
    ['Link', tune.link1],
    ['Link', tune.link2],
    ['Link', tune.link3],
    ['Link', (tune.links || []).join(' ')],
    ['Tags', Array.isArray(tune.customTags) ? tune.customTags.join(' ') : ''],
    ['Shared With', Array.isArray(tune.sharedWith) ? tune.sharedWith.map(s => `${s.displayName || ''} ${s.username || ''}`).join(' ') : ''],
    ['Imported By', `${tune.importedFrom?.displayName || ''} ${tune.importedFrom?.username || ''}`],
    ['Attachment', Array.isArray(tune.attachments) ? tune.attachments.map(a => a?.name || '').join(' ') : ''],
    ['Recording', Array.isArray(tune.recordings) ? tune.recordings.map(r => `${r?.label || ''} ${r?.name || ''} ${r?.url || ''}`).join(' ') : ''],
  ];
  return fields
    .map(([label, value]) => [label, typeof value === 'string' ? toPlainText(value) : ''])
    .map(([label, value]) => {
      if (!value) return null;
      const lower = value.toLowerCase();
      const matchedTokens = tokens.filter(token => lower.includes(token));
      if (!matchedTokens.length) return null;
      const firstIndex = matchedTokens
        .map(token => lower.indexOf(token))
        .filter(index => index >= 0)
        .reduce((min, index) => (index < min ? index : min), Number.POSITIVE_INFINITY);
      return {
        label,
        value,
        matchedTokens,
        tokenCount: matchedTokens.length,
        phraseMatch: q ? lower.includes(q) : false,
        firstIndex: Number.isFinite(firstIndex) ? firstIndex : 999999,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.tokenCount !== b.tokenCount) return b.tokenCount - a.tokenCount;
      if (a.phraseMatch !== b.phraseMatch) return Number(b.phraseMatch) - Number(a.phraseMatch);
      return a.firstIndex - b.firstIndex;
    });
};

const renderHighlighted = (text, rawQuery) => {
  const tokens = getQueryTokens(rawQuery);
  if (!tokens.length) return text;
  const orderedTokens = [...tokens].sort((a, b) => b.length - a.length);
  const tokenSet = new Set(tokens);
  const re = new RegExp(`(${orderedTokens.map(escapeRegExp).join('|')})`, 'ig');
  const parts = String(text || '').split(re);
  return parts.map((part, idx) =>
    tokenSet.has(part.toLowerCase())
      ? (
        <mark key={idx} style={{ background: 'rgba(255, 214, 10, 0.45)', padding: '0 1px' }}>
          {part}
        </mark>
      )
      : <React.Fragment key={idx}>{part}</React.Fragment>
  );
};

const getSearchScore = (matches, rawQuery) => {
  const tokens = getQueryTokens(rawQuery);
  if (!tokens.length || !matches.length) {
    return { tokenCount: 0, coverage: 0, phraseMatch: false, firstIndex: 999999 };
  }
  const matchedTokens = new Set();
  let phraseMatch = false;
  let firstIndex = 999999;

  matches.forEach(match => {
    (match.matchedTokens || []).forEach(token => matchedTokens.add(token));
    phraseMatch = phraseMatch || Boolean(match.phraseMatch);
    if (typeof match.firstIndex === 'number' && match.firstIndex < firstIndex) {
      firstIndex = match.firstIndex;
    }
  });

  const tokenCount = matchedTokens.size;
  return {
    tokenCount,
    coverage: tokenCount / tokens.length,
    phraseMatch,
    firstIndex,
  };
};

const toDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return isNaN(d) ? null : d;
  }
  if (typeof value === 'string') {
    const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
    return isNaN(d) ? null : d;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d) ? null : d;
  }
  return null;
};

const getTimeFieldDate = (tune, basis) => {
  if (basis === 'viewed') return toDateValue(tune.lastViewedAt);
  if (basis === 'created') return toDateValue(tune.createdAt);
  if (basis === 'imported') return toDateValue(tune.importedOn) || toDateValue(tune.createdAt);
  if (basis === 'event') return toDateValue(tune.eventDate);
  return toDateValue(tune.updatedAt) || toDateValue(tune.createdAt);
};

const matchesTimeRange = (tune, range, basis, selectedMonth, selectedYear) => {
  if (!range) return true;
  const date = getTimeFieldDate(tune, basis);
  if (!date) return false;

  const now = new Date();
  if (range === 'past-month') {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return date >= start && date <= now;
  }
  if (range === 'this-month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return date >= start && date <= now;
  }
  if (range === 'past-week') {
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    return date >= start && date <= now;
  }
  if (range === 'past-year') {
    const start = new Date(now);
    start.setDate(start.getDate() - 365);
    return date >= start && date <= now;
  }
  if (range === 'year') {
    if (!selectedYear) return true;
    const year = Number(selectedYear);
    if (!year) return true;
    return date.getFullYear() === year;
  }
  if (range === 'month') {
    if (!selectedMonth) return true;
    const [y, m] = String(selectedMonth).split('-').map(Number);
    if (!y || !m) return true;
    return date.getFullYear() === y && (date.getMonth() + 1) === m;
  }
  return true;
};

const buildSnippetForMatch = (value, matchedTokens = []) => {
  const text = String(value || '');
  if (!text) return { text: '', prefix: '', suffix: '' };
  if (!matchedTokens.length) {
    const maxLen = 140;
    return {
      text: text.slice(0, maxLen),
      prefix: '',
      suffix: text.length > maxLen ? '…' : '',
    };
  }

  const lower = text.toLowerCase();
  const orderedTokens = matchedTokens.map(t => String(t || '').toLowerCase()).filter(Boolean);
  const chain = [];
  let cursor = 0;
  orderedTokens.forEach((token) => {
    let idx = lower.indexOf(token, cursor);
    if (idx < 0) idx = lower.indexOf(token);
    if (idx >= 0) {
      chain.push({ token, idx });
      cursor = idx + token.length;
    }
  });

  if (!chain.length) {
    const maxLen = 140;
    return {
      text: text.slice(0, maxLen),
      prefix: '',
      suffix: text.length > maxLen ? '…' : '',
    };
  }

  const first = chain[0];
  const last = chain[chain.length - 1];
  const firstPos = first.idx;
  const lastPos = last.idx + String(last.token || '').length;

  let start = Math.max(0, firstPos - 35);
  let end = Math.min(text.length, lastPos + 45);

  const maxWindow = 320;
  if (end - start > maxWindow) {
    end = Math.min(text.length, start + maxWindow);
  }

  return {
    text: text.slice(start, end),
    prefix: start > 0 ? '…' : '',
    suffix: end < text.length ? '…' : '',
  };
};

const getNotePreviewText = (value = '') => toPlainText(value);
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const normalizeTimeRange = (value) => {
  if (!value || value === 'any') return '';
  if (value === 'this-week') return 'past-week';
  if (value === 'last-30') return 'past-month';
  if (value === 'last-year') return 'past-year';
  return value;
};

export default function SearchPage() {
  const { user } = useAuth();

  const { inputStyle, textStyle } = useUI();

  const { activeSession } = useActiveSession();
  const [tunes, setTunes] = useState([]);
  const [selectedTuneIds, setSelectedTuneIds] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const navigate = useNavigate();
const [searchQuery, setSearchQuery] = useState(null);
const [filterKey, setFilterKey] = useState(null);
const [filterType, setFilterType] = useState(null);
const [filterGenre, setFilterGenre] = useState(null);
const [filterStatus, setFilterStatus] = useState('—');
const [filterRating, setFilterRating] = useState('—');
const [filterRatingLabel, setFilterRatingLabel] = useState('—');
const [filterTimeRange, setFilterTimeRange] = useState('');
const [filterTimeBasis, setFilterTimeBasis] = useState('updated');
const [filterTimeMonth, setFilterTimeMonth] = useState('');
const [filterTimeYear, setFilterTimeYear] = useState('');
const [showTimeFilters, setShowTimeFilters] = useState(false);
const [sessionConfirmations, setSessionConfirmations] = useState({});
const [selectedTags, setSelectedTags] = useState([]);
const [tagLogic, setTagLogic] = useState('AND'); // or 'OR'
const [showTagFilters, setShowTagFilters] = useState(false);
const [showBulkTagInput, setShowBulkTagInput] = useState(false);
const [bulkTags, setBulkTags] = useState([]);
const [showBulkAddToSetPicker, setShowBulkAddToSetPicker] = useState(false);
const [bulkAddToSetQuery, setBulkAddToSetQuery] = useState('');
const [bulkAddTargetSets, setBulkAddTargetSets] = useState([]);
const [bulkAddLoading, setBulkAddLoading] = useState(false);
const [dexieReady, setDexieReady] = useState(false);
const location = useLocation();
const [filterSentBy, setFilterSentBy] = useState('');
const [swipeSendTarget, setSwipeSendTarget] = useState(null); // tune being sent via swipe
const [showBulkSendPicker, setShowBulkSendPicker] = useState(false);
const [showBulkExportModal, setShowBulkExportModal] = useState(false);
const [bulkExportTunes, setBulkExportTunes] = useState([]);
const [filtered, setFiltered] = useState([]);
const [pendingSessionTune, setPendingSessionTune] = useState(null);
const [filtersLoaded, setFiltersLoaded] = useState(false);
const [notice, setNotice] = useState(null);
const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
const [showQuickAddNote, setShowQuickAddNote] = useState(false);
const [isMobileFilterLayout, setIsMobileFilterLayout] = useState(
  typeof window !== 'undefined' ? window.innerWidth <= 600 : false
);
const [isNarrowCardLayout, setIsNarrowCardLayout] = useState(
  typeof window !== 'undefined' ? window.innerWidth <= 900 : false
);
const resultsContainerRef = useRef(null);
const confirmActionRef = useRef(null);
const previousModeRef = useRef(null);
const [scrollAreaHeight, setScrollAreaHeight] = useState(320);
const updateScrollHeight = React.useCallback(() => {
  if (!resultsContainerRef.current) return;
  const rect = resultsContainerRef.current.getBoundingClientRect();
  setScrollAreaHeight(window.innerHeight - rect.top - 8);
}, []);
useEffect(() => {
  updateScrollHeight();
  window.addEventListener('resize', updateScrollHeight);
  return () => window.removeEventListener('resize', updateScrollHeight);
}, [updateScrollHeight]);
const [actionConfirm, setActionConfirm] = useState({
  open: false,
  title: 'Confirm Action',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  danger: false,
});

const requestActionConfirm = ({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false }, action) => {
  confirmActionRef.current = action;
  setActionConfirm({
    open: true,
    title,
    message,
    confirmLabel,
    cancelLabel,
    danger,
  });
};

const handleActionConfirm = async () => {
  const action = confirmActionRef.current;
  confirmActionRef.current = null;
  setActionConfirm(prev => ({ ...prev, open: false }));
  if (typeof action === 'function') {
    await action();
  }
};

const cancelActionConfirm = () => {
  confirmActionRef.current = null;
  setActionConfirm(prev => ({ ...prev, open: false }));
};


useEffect(() => {
  dexie.open().then(() => setDexieReady(true));
}, []);

// Sync sentBy filter from URL params (deep link from detail pages)
useEffect(() => {
  const params = new URLSearchParams(location.search);
  const sb = params.get('sentBy') || '';
  if (sb) setFilterSentBy(sb);
}, [location.search]);

  const [sortOrder, setSortOrder] = useState('newestAdded');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => {
    const stored = getStoredAdvancedFiltersOpen('tunes');
    return typeof stored === 'boolean' ? stored : false;
  });
  const [showAdvancedLockPopup, setShowAdvancedLockPopup] = useState(false);
  const [showTSImportModal, setShowTSImportModal] = useState(false);
const maybePrefsHook = useUserPrefs();
const prefs = user ? maybePrefsHook?.prefs : null;
const { WhatsNewBanner, NudgeBanner, isSimpleMode, unlockFullMode } = useOnboarding();
const labels = maybePrefsHook?.labels;
const savePrefs = maybePrefsHook?.savePrefs;
const modeGenres = maybePrefsHook?.modeGenres || [];
const modeTypes = maybePrefsHook?.modeTypes || [];
const currentMode = prefs?.appMode || 'tunes';
const hasImportedNotes = tunes.some((t) => (t.mode || 'tunes') === 'notes' && Boolean(t.importedOn));
const helperFilterText =
  currentMode === 'notes'
    ? 'Use the filters above to narrow your library by category, type, status, rating, or tags. Filters persist between visits.'
    : currentMode === 'all'
      ? 'Use the filters above to narrow your library by genre/category, type, key, status, rating, or tags. Filters persist between visits.'
      : 'Use the filters above to narrow your library by genre, type, key, status, rating, or tags. Filters persist between visits.';
const searchPlaceholder =
  currentMode === 'notes'
    ? 'Search any field (title, category, type, notes, tags, links...)'
    : currentMode === 'all'
      ? 'Search any field (title, category/genre, type, notes, tags, links...)'
      : 'Search any field (title, genre, key, type, abc, lyrics, notes...)';
const supportsEventDateBasis = currentMode === 'notes' || currentMode === 'all';
const supportsImportedBasis = currentMode === 'notes' || currentMode === 'all';
const activeUiSize =
  (typeof document !== 'undefined' && document.body?.dataset?.uiSize) ||
  (typeof window !== 'undefined' ? localStorage.getItem('uiSize') : null) ||
  'medium';
const isMobileLargeUi = isMobileFilterLayout && activeUiSize === 'large';
const useCompactBulkLabel = isMobileFilterLayout && activeUiSize !== 'small';
const bulkLabelBase = useCompactBulkLabel ? 'Actions' : 'Bulk Actions';
const hasActiveTimeFilter =
  !!filterTimeRange;
const hasAdvancedFiltersActive =
  Boolean(filterStatus) ||
  Boolean(filterRating) ||
  Boolean(filterSentBy) ||
  hasActiveTimeFilter ||
  selectedTags.length > 0;
// Collapse advanced filters when switching to simple mode
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { if (isSimpleMode) setShowAdvancedFilters(false); }, [isSimpleMode]);
const selectedDeleteItemLabel = (() => {
  const selected = tunes.filter((t) => selectedTuneIds.includes(t.id));
  const modeSet = new Set(selected.map((t) => t.mode || 'tunes'));
  if (modeSet.size > 1) return { singular: 'item', plural: 'items' };
  if (modeSet.has('notes')) return { singular: 'note', plural: 'notes' };
  return { singular: 'tune', plural: 'tunes' };
})();
const formatMonthLabel = (monthValue) => {
  if (!monthValue) return 'Selected month';
  const [y, m] = String(monthValue).split('-').map(Number);
  if (!y || !m) return 'Selected month';
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
const formatYearLabel = (yearValue) => {
  const y = Number(yearValue);
  return y ? String(y) : 'Selected year';
};
const formatShortUsDate = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return '';
  const mm = String(date.getMonth() + 1);
  const dd = String(date.getDate());
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};
const timeRangeLabel =
  filterTimeRange === 'past-week' ? 'Past Week'
    : filterTimeRange === 'this-month' ? 'This Month'
      : filterTimeRange === 'past-month' ? 'Past Month'
        : filterTimeRange === 'past-year' ? 'Past Year'
          : filterTimeRange === 'year' ? formatYearLabel(filterTimeYear)
        : filterTimeRange === 'month' ? formatMonthLabel(filterTimeMonth)
          : 'No Time Filter';
const timeBasisLabel =
  filterTimeBasis === 'viewed' ? 'Viewed'
    : filterTimeBasis === 'created' ? 'Created'
      : filterTimeBasis === 'imported' ? 'Imported'
      : filterTimeBasis === 'event' ? 'Event'
        : 'Updated';
const timeSummaryLabel = !hasActiveTimeFilter
  ? 'Time'
  : `Time: ${timeBasisLabel} ${timeRangeLabel}`;
const compactTimeSummaryLabel = !hasActiveTimeFilter
  ? 'Time'
  : `${timeBasisLabel} ${timeRangeLabel}`;
const timeRangeOptions = [
  { value: 'past-week', label: 'Past week' },
  { value: 'this-month', label: 'This month' },
  { value: 'past-month', label: 'Past month' },
  { value: 'past-year', label: 'Past year' },
  { value: 'year', label: 'Pick year' },
  { value: 'month', label: 'Pick month' }
];
const timeBasisOptions = [
  { value: 'updated', label: 'Updated' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'created', label: 'Created' },
  ...(supportsImportedBasis ? [{ value: 'imported', label: 'Imported' }] : []),
  ...(supportsEventDateBasis ? [{ value: 'event', label: 'Event' }] : [])
];
const [bulkActionValue, setBulkActionValue] = useState('');
const [monthPickerYear, setMonthPickerYear] = useState(new Date().getFullYear());
const handleTimeRangeSelect = (value) => {
  setFilterTimeRange(value);
  if (value === 'month') {
    const [y] = (filterTimeMonth || '').split('-').map(Number);
    setMonthPickerYear(y || new Date().getFullYear());
    setFilterTimeYear('');
  } else if (value === 'year') {
    setMonthPickerYear(Number(filterTimeYear) || new Date().getFullYear());
    setFilterTimeMonth('');
  } else {
    setFilterTimeMonth('');
    setFilterTimeYear('');
  }
};

useEffect(() => {
  const unsupportedBasis = (
    (!supportsEventDateBasis && filterTimeBasis === 'event') ||
    (!supportsImportedBasis && filterTimeBasis === 'imported')
  );
  if (unsupportedBasis) {
    setFilterTimeBasis('updated');
  }
}, [supportsEventDateBasis, supportsImportedBasis, filterTimeBasis]);

useEffect(() => {
  const normalizedSortOrder = sortOrder === 'newest'
    ? 'newestAdded'
    : sortOrder === 'oldest'
      ? 'oldestAdded'
      : sortOrder;
  const importedSortSelected = normalizedSortOrder === 'newestImported' || normalizedSortOrder === 'oldestImported';
  const importedSortSupported = hasImportedNotes && (currentMode === 'notes' || currentMode === 'all');
  if (importedSortSelected && !importedSortSupported) {
    setSortOrder('newestAdded');
    return;
  }
  if (normalizedSortOrder === 'oldestImported') {
    setSortOrder('newestImported');
  }
}, [sortOrder, hasImportedNotes, currentMode]);

useEffect(() => {
  if (isSimpleMode) return;
  const stored = getStoredAdvancedFiltersOpen(currentMode);
  if (typeof stored === 'boolean') {
    setShowAdvancedFilters(stored);
  }
}, [currentMode, isSimpleMode]);

useEffect(() => {
  if (isSimpleMode) return;
  storeAdvancedFiltersOpen(currentMode, showAdvancedFilters);
}, [currentMode, showAdvancedFilters, isSimpleMode]);

useEffect(() => {
  if (previousModeRef.current === null) {
    previousModeRef.current = currentMode;
    return;
  }
  if (previousModeRef.current === currentMode) return;
  previousModeRef.current = currentMode;
  setSelectedTuneIds([]);
  setBulkActionValue('');
  setShowBulkTagInput(false);
  setBulkTags([]);
  setShowBulkAddToSetPicker(false);
  setBulkAddToSetQuery('');
  setShowBulkSendPicker(false);
  setShowBulkExportModal(false);
  setShowDeleteSelectedModal(false);
}, [currentMode]);

useEffect(() => {
  const onResize = () => {
    setIsMobileFilterLayout(window.innerWidth <= 600);
    setIsNarrowCardLayout(window.innerWidth <= 900);
  };
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}, []);

const showNotice = (text, tone = 'info') => setNotice({ text, tone });

useEffect(() => {
  if (!notice) return undefined;
  const timer = setTimeout(() => setNotice(null), 2600);
  return () => clearTimeout(timer);
}, [notice]);

 useEffect(() => {
  if (!user) return;

  const applyFilters = (savedFilters) => {
    const urlParams = new URLSearchParams(window.location.search);
    const tagParam = urlParams.get('tag');
    const genreParam = urlParams.get('genre');
    const typeParam = urlParams.get('type');

    if (tagParam || genreParam || typeParam) {
      setSearchQuery('');
      setFilterKey('');
      setFilterType(typeParam ? normalizeType(typeParam) : '');
      setFilterGenre(genreParam ? normalizeGenre(genreParam) : '');
      setFilterStatus('');
      setFilterRating('');
      setFilterRatingLabel('');
      setFilterTimeRange('');
      setFilterTimeBasis('updated');
      setFilterTimeMonth('');
      setFilterTimeYear('');
      setSelectedTags(tagParam ? [normalizeTag(tagParam)] : []);
      setTagLogic('AND');
      setShowTagFilters(Boolean(tagParam));
    } else {
      setSearchQuery(savedFilters.searchQuery ?? '');
      setFilterKey(savedFilters.filterKey ?? '');
      setFilterType(savedFilters.filterType ?? '');
      setFilterGenre(savedFilters.filterGenre ?? '');
      setFilterStatus(savedFilters.filterStatus ?? '');
      setFilterRating(savedFilters.filterRating ?? '');
      setFilterRatingLabel(savedFilters.filterRatingLabel ?? '');
      setFilterTimeRange(normalizeTimeRange(savedFilters.filterTimeRange || ''));
      setFilterTimeBasis(savedFilters.filterTimeBasis || 'updated');
      setFilterTimeMonth(savedFilters.filterTimeMonth || '');
      setFilterTimeYear(savedFilters.filterTimeYear || '');
      setSelectedTags(Array.isArray(savedFilters.selectedTags) ? normalizeTagsArray(savedFilters.selectedTags) : []);
      setTagLogic(savedFilters.tagLogic || 'AND');
      setShowTagFilters(Array.isArray(savedFilters.selectedTags) && savedFilters.selectedTags.length > 0);
    }
    setFiltersLoaded(true);
  };

  const loadFilters = async () => {
    const localBundle = readLocalSearchFilterBundle();
    try {
      if (navigator.onLine) {
        const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : {};
        const byMode = (data.searchFiltersByMode && typeof data.searchFiltersByMode === 'object')
          ? data.searchFiltersByMode
          : localBundle.byMode;
        const lastPureMode = normalizeModeKey(data.searchFiltersLastPureMode || localBundle.lastPureMode || 'tunes');
        const savedFilters = getFiltersForMode(byMode, currentMode, lastPureMode);
        const fallbackLegacy = data.searchFilters || localBundle.legacy || {};
        applyFilters(hasFilterValues(savedFilters) ? savedFilters : fallbackLegacy);
      } else {
        const localSaved = getFiltersForMode(localBundle.byMode, currentMode, localBundle.lastPureMode);
        applyFilters(hasFilterValues(localSaved) ? localSaved : localBundle.legacy || {});
      }
    } catch (err) {
      console.error('Failed to load filters:', err);
      const localSaved = getFiltersForMode(localBundle.byMode, currentMode, localBundle.lastPureMode);
      applyFilters(hasFilterValues(localSaved) ? localSaved : localBundle.legacy || {});
    }
  };

  loadFilters();
}, [user, currentMode]);


useEffect(() => {
  if (!user || !filtersLoaded) return;
  saveCurrentFilters();
}, [
  user,
  filtersLoaded,
  searchQuery,
  filterKey,
  filterType,
  filterGenre,
  filterStatus,
  filterRating,
  filterRatingLabel,
  filterTimeRange,
  filterTimeBasis,
  filterTimeMonth,
  filterTimeYear,
  selectedTags,
  tagLogic
]);



const clearAllFilters = () => {
  setSearchQuery('');
  setFilterKey('');
  setFilterType('');
  setFilterGenre('');
  setFilterStatus('');
  setFilterRating('');
  setFilterRatingLabel('');
  setFilterTimeRange('');
  setFilterTimeBasis('updated');
  setFilterTimeMonth('');
  setFilterTimeYear('');
  setShowTimeFilters(false);
  setShowTagFilters(false);
  setSelectedTags([]);        // ✅ clear tag pills
  setTagLogic('AND');         // ✅ optionally reset logic too
  setFilterSentBy('');
};

const addTagFromPreview = (tag) => {
  const normalized = normalizeTag(tag || '');
  if (!normalized) return;
  setShowTagFilters(true);
  setSelectedTags((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
};

const availableFilterTags = React.useMemo(() => {
  const fromPrefs = Array.isArray(prefs?.customTags) ? prefs.customTags : [];
  const fromTunes = tunes.flatMap((t) => (Array.isArray(t.customTags) ? t.customTags : []));
  return normalizeTagsArray([...fromPrefs, ...fromTunes]).sort((a, b) => a.localeCompare(b));
}, [prefs?.customTags, tunes]);

const hasActiveSearchFilters = Boolean(
  (searchQuery || '').trim() ||
  filterKey ||
  filterType ||
  filterGenre ||
  filterStatus ||
  filterRating ||
  filterRatingLabel ||
  filterSentBy ||
  filterTimeRange ||
  filterTimeMonth ||
  filterTimeYear ||
  filterTimeBasis !== 'updated' ||
  selectedTags.length > 0
);







  const getFilteredSubset = (ignoreField) => {
  return tunes.filter(tune => {
    const matchesSearch = getSearchMatches(tune, searchQuery).length > 0 || !(searchQuery || '').trim();

    const nk = normalizeKey(tune.key);
    const matchMap = {
      genre: !filterGenre || (tune.genre && tune.genre.toLowerCase() === filterGenre.toLowerCase()),
      type: !filterType || (tune.type && normalizeType(tune.type) === normalizeType(filterType)),
      key: !filterKey || nk === filterKey || (filterKey.startsWith('root:') && keyRoot(nk) === filterKey.replace('root:', '')),
      status: !filterStatus || (filterStatus === '—' ? !tune.status : tune.status === filterStatus),
      rating: !filterRating || (filterRating === '—' ? !tune.rating : tune.rating === filterRating),
      ratingLabel: !filterRatingLabel || (filterRatingLabel === '—' ? !tune.ratingLabel : tune.ratingLabel === filterRatingLabel),
      time: matchesTimeRange(tune, filterTimeRange, filterTimeBasis, filterTimeMonth, filterTimeYear),
    };

    delete matchMap[ignoreField];

    const restMatch = Object.values(matchMap).every(Boolean);

    return matchesSearch && restMatch;
  });
};


  const keyGroups = {};
  tunes.forEach(t => {
    const nk = normalizeKey(t.key);
    if (!nk) return;
    const root = keyRoot(nk);
    if (!keyGroups[root]) keyGroups[root] = new Set();
    keyGroups[root].add(nk);
  });

  const filteredTunes = tunes.filter(tune => {
    if (currentMode !== 'all' && (tune.mode || 'tunes') !== currentMode) return false;
    const tagMatch = (tune) => {
      if (selectedTags.length === 0) return true;
      const tags = normalizeTagsArray(tune.customTags || []);
      const selected = normalizeTagsArray(selectedTags);
      return tagLogic === 'AND'
        ? selected.every(tag => tags.includes(tag))
        : selected.some(tag => tags.includes(tag));
    };

    const matchesSearch = getSearchMatches(tune, searchQuery).length > 0 || !(searchQuery || '').trim();
    const nk = normalizeKey(tune.key);
    const matchesKey = !filterKey
      || nk === filterKey
      || (filterKey.startsWith('root:') && keyRoot(nk) === filterKey.replace('root:', ''));
    const matchesType = !filterType || (tune.type && normalizeType(tune.type) === normalizeType(filterType));
    const matchesGenre = !filterGenre || normalizeGenre(tune.genre) === filterGenre;
    const matchesStatus =
      !filterStatus ||
      (filterStatus === '—' ? !tune.status : tune.status === filterStatus);

    const matchesRating =
      !filterRating ||
      (filterRating === '—' ? !tune.rating : tune.rating === filterRating);
    const matchesTime = matchesTimeRange(tune, filterTimeRange, filterTimeBasis, filterTimeMonth, filterTimeYear);

    const matchesTags = tagMatch(tune);
    const matchesSentBy = !filterSentBy || (tune.importedFrom?.uid === filterSentBy);

return (
  matchesSearch &&
  matchesKey &&
  matchesType &&
  matchesGenre &&
  matchesStatus &&
  matchesRating &&
  matchesTime &&
  matchesTags &&
  matchesSentBy
);

  });

  const sortedTunes = React.useMemo(() => {
    const tunesCopy = [...filteredTunes];
    const q = (searchQuery || '').trim();
    const hasQuery = q.length > 0;
    const baseComparator = (a, b) => {
      const normalizedSortOrder = sortOrder === 'newest'
        ? 'newestAdded'
        : sortOrder === 'oldest'
          ? 'oldestAdded'
          : sortOrder === 'oldestImported'
            ? 'newestImported'
          : sortOrder;
      if (normalizedSortOrder === 'alpha') {
        return a.title.localeCompare(b.title);
      }
      if (normalizedSortOrder === 'alphaDesc') {
        return b.title.localeCompare(a.title);
      }
      const sortByImported = normalizedSortOrder === 'newestImported';
      const ascending = normalizedSortOrder === 'oldestAdded';
      const getTime = (tune) => {
        const ts = sortByImported
          ? (tune.importedOn || tune.createdAt)
          : tune.createdAt;
        if (ts?.toMillis) return ts.toMillis();
        if (ts?.toDate) return ts.toDate().getTime();
        if (ts !== null && typeof ts === 'object' && typeof ts.seconds === 'number') {
          return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
        }
        if (typeof ts === 'string') {
          const parsed = Date.parse(ts);
          return isNaN(parsed) ? 0 : parsed;
        }
        if (typeof ts === 'number') return ts;
        return 0;
      };
      const aTime = getTime(a);
      const bTime = getTime(b);
      if (aTime === bTime) return a.id.localeCompare(b.id);
      return ascending ? aTime - bTime : bTime - aTime;
    };

    if (hasQuery) {
      const scored = tunesCopy.map((tune) => {
        const matches = getSearchMatches(tune, q);
        return { tune, score: getSearchScore(matches, q) };
      });
      scored.sort((a, b) => {
        if (a.score.tokenCount !== b.score.tokenCount) {
          return b.score.tokenCount - a.score.tokenCount;
        }
        if (a.score.coverage !== b.score.coverage) {
          return b.score.coverage - a.score.coverage;
        }
        if (a.score.phraseMatch !== b.score.phraseMatch) {
          return Number(b.score.phraseMatch) - Number(a.score.phraseMatch);
        }
        if (a.score.firstIndex !== b.score.firstIndex) {
          return a.score.firstIndex - b.score.firstIndex;
        }
        return baseComparator(a.tune, b.tune);
      });
      return scored.map(item => item.tune);
    }

    // Pinned tunes always float to the top (no-query view only)
    tunesCopy.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return baseComparator(a, b);
    });

    return tunesCopy;
  }, [filteredTunes, sortOrder, searchQuery]);

  const searchMatchMap = React.useMemo(() => {
    const map = new Map();
    const q = (searchQuery || '').trim();
    if (!q) return map;
    for (const t of sortedTunes) {
      const matches = getSearchMatches(t, q);
      if (matches.length) map.set(t.id, matches);
    }
    return map;
  }, [sortedTunes, searchQuery]);

  const toggleTuneSelection = (id) => {
    setSelectedTuneIds(prev =>
      prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]
    );
  };

  const visibleTuneIds = sortedTunes.map((t) => t.id);
  const allVisibleTunesSelected = visibleTuneIds.length > 0 && visibleTuneIds.every((id) => selectedTuneIds.includes(id));

  const toggleSelectAll = () => {
    if (allVisibleTunesSelected) {
      setSelectedTuneIds((prev) => prev.filter((id) => !visibleTuneIds.includes(id)));
    } else {
      setSelectedTuneIds((prev) => Array.from(new Set([...prev, ...visibleTuneIds])));
    }
  };

  const executeDeleteSelectedTunes = async () => {
  if (!user || selectedTuneIds.length === 0) return;

  const tunesToDelete = [];

  for (const id of selectedTuneIds) {
    let data;
    if (navigator.onLine) {
      const ref = doc(db, 'users', user.uid, 'tunes', id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        data = snap.data();
      }
    } else {
      data = await dexie.tunes.get(id);
    }

    if (data) {
      tunesToDelete.push({ id, data });
    }
  }

  let affectedSets = [];
  if (navigator.onLine) {
    const setsSnapshot = await getDocs(collection(db, 'users', user.uid, 'sets'));
    setsSnapshot.forEach(docSnap => {
      const set = docSnap.data();
      const matchingTunes = (set.tunes || []).filter(t => selectedTuneIds.includes(t.id));
      if (matchingTunes.length > 0) {
        affectedSets.push({
          id: docSnap.id,
          title: set.title,
          mode: set.mode || 'tunes',
          originalTunes: set.tunes,
          removedTunes: matchingTunes,
        });
      }
    });
  } else {
    const allSets = await dexie.sets.toArray();
    affectedSets = allSets
      .filter(set => (set.tunes || []).some(t => selectedTuneIds.includes(t.id)))
      .map(set => ({
        id: set.id,
        title: set.title,
        mode: set.mode || 'tunes',
        originalTunes: set.tunes,
        removedTunes: set.tunes.filter(t => selectedTuneIds.includes(t.id)),
      }));
  }

  setPendingDelete({ tunes: tunesToDelete, sets: affectedSets });
  setSelectedTuneIds([]);

  const timer = setTimeout(async () => {
    for (const { id } of tunesToDelete) {
      if (navigator.onLine) {
        await deleteDoc(doc(db, 'users', user.uid, 'tunes', id));
      } else {
        await dexie.pendingWrites.put({
          id,
          type: 'tune',
          operation: 'delete',
        });
      }
      await dexie.tunes.delete(id);
    }

    for (const set of affectedSets) {
      const updatedTunes = (set.originalTunes || []).filter(t => !selectedTuneIds.includes(t.id));

      if (navigator.onLine) {
        const setRef = doc(db, 'users', user.uid, 'sets', set.id);
        try {
          const setSnap = await getDoc(setRef);
          if (setSnap.exists()) {
            await updateDoc(setRef, { tunes: updatedTunes });
          }
        } catch (err) {
          console.warn(`Skipping set cleanup for missing/deleted set ${set.id}:`, err);
        }
      } else {
        const setData = await dexie.sets.get(set.id);
        if (setData) {
          await dexie.sets.put({
            ...setData,
            tunes: updatedTunes
          });
          await dexie.pendingWrites.put({
            id: set.id,
            type: 'set',
            operation: 'update',
            data: { ...setData, tunes: updatedTunes }
          });
        }
      }
    }

    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);

  setUndoTimer(timer);
};

  const deleteSelectedTunes = () => {
    if (!user || selectedTuneIds.length === 0) return;
    setShowDeleteSelectedModal(true);
  };

const undoDelete = async () => {
  if (!user || !pendingDelete) return;
  clearTimeout(undoTimer);

  for (const { id, data } of pendingDelete.tunes) {
    if (navigator.onLine) {
      await setDoc(doc(db, 'users', user.uid, 'tunes', id), data);
    }
    await dexie.tunes.put({ id, ...data });
  }

  for (const set of pendingDelete.sets) {
    const mergedTunes = [...(set.originalTunes || []), ...set.removedTunes];
    const dedupedTunes = Array.from(
      new Map(mergedTunes.map(t => [t.id, t])).values()
    );

    if (navigator.onLine) {
      const setRef = doc(db, 'users', user.uid, 'sets', set.id);
      const snap = await getDoc(setRef);
      if (snap.exists()) {
        await updateDoc(setRef, { tunes: dedupedTunes });
      }
    }

    const localSet = await dexie.sets.get(set.id);
    if (localSet) {
      await dexie.sets.put({
        ...localSet,
        tunes: dedupedTunes
      });

      if (!navigator.onLine) {
        await dexie.pendingWrites.put({
          id: set.id,
          type: 'set',
          operation: 'update',
          data: {
            ...localSet,
            tunes: dedupedTunes
          }
        });
      }
    }
  }

  setPendingDelete(null);
  setUndoTimer(null);
};

// ── Swipe-row actions ────────────────────────────────────────────────────────

const swipeDeleteTune = async (tune) => {
  if (!user) return;
  // Fetch affected sets upfront so they can be restored on undo
  let affectedSets = [];
  if (navigator.onLine) {
    const setsSnap = await getDocs(collection(db, 'users', user.uid, 'sets'));
    setsSnap.forEach(docSnap => {
      const set = docSnap.data();
      const removed = (set.tunes || []).filter(t => t.id === tune.id);
      if (removed.length > 0) {
        affectedSets.push({ id: docSnap.id, title: set.title, originalTunes: set.tunes, removedTunes: removed });
      }
    });
  } else {
    const allSets = await dexie.sets.toArray();
    affectedSets = allSets
      .filter(s => (s.tunes || []).some(t => t.id === tune.id))
      .map(s => ({ id: s.id, title: s.title, originalTunes: s.tunes, removedTunes: s.tunes.filter(t => t.id === tune.id) }));
  }

  const tuneId = tune.id;
  const tuneData = { ...tune };
  setPendingDelete({ tunes: [{ id: tuneId, data: tuneData }], sets: affectedSets });

  const timer = setTimeout(async () => {
    if (navigator.onLine) {
      await deleteDoc(doc(db, 'users', user.uid, 'tunes', tuneId));
    } else {
      await dexie.pendingWrites.put({ id: tuneId, type: 'tune', operation: 'delete' });
    }
    await dexie.tunes.delete(tuneId);

    for (const set of affectedSets) {
      const updatedTunes = (set.originalTunes || []).filter(t => t.id !== tuneId);
      if (navigator.onLine) {
        try {
          const setRef = doc(db, 'users', user.uid, 'sets', set.id);
          const snap = await getDoc(setRef);
          if (snap.exists()) await updateDoc(setRef, { tunes: updatedTunes });
        } catch (_) {}
      } else {
        const setData = await dexie.sets.get(set.id);
        if (setData) {
          await dexie.sets.put({ ...setData, tunes: updatedTunes });
          await dexie.pendingWrites.put({ id: set.id, type: 'set', operation: 'update', data: { ...setData, tunes: updatedTunes } });
        }
      }
    }
    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);

  setUndoTimer(timer);
};

const swipePinTune = async (tune) => {
  if (!user) return;
  const newPinned = !tune.pinned;
  if (navigator.onLine) {
    await updateDoc(doc(db, 'users', user.uid, 'tunes', tune.id), { pinned: newPinned });
  }
  await dexie.tunes.update(tune.id, { pinned: newPinned });
};

const handleSwipeSendTune = async (recipientUid, contact) => {
  if (!user) return;
  const myProfile = await getUserProfile(user.uid);
  await sendTuneToUser(user.uid, myProfile, recipientUid, swipeSendTarget.id, swipeSendTarget.title);
  // Record sharedWith on the tune
  const tuneRef = doc(db, 'users', user.uid, 'tunes', swipeSendTarget.id);
  const tuneSnap = await getDoc(tuneRef);
  if (tuneSnap.exists()) {
    const existing = tuneSnap.data().sharedWith || [];
    if (!existing.some(s => s.uid === recipientUid)) {
      await updateDoc(tuneRef, {
        sharedWith: [...existing, {
          uid: recipientUid,
          displayName: contact?.displayName || '',
          username: contact?.username || '',
          sentAt: new Date().toISOString()
        }],
        updatedAt: new Date().toISOString()
      });
    }
  }
};

// ─────────────────────────────────────────────────────────────────────────────

const updateStatusForSelectedTunes = async (newStatus) => {
  if (!user || selectedTuneIds.length === 0) return;

  requestActionConfirm(
    {
      title: 'Apply Bulk Status',
      message: `Set status to "${newStatus}" for ${selectedTuneIds.length} selected ${(labels?.items || 'tunes')}?`,
      confirmLabel: 'Apply',
      cancelLabel: 'Cancel',
    },
    async () => {
      try {
        for (const id of selectedTuneIds) {
          const ref = doc(db, 'users', user.uid, 'tunes', id);
          const updated = { status: newStatus };

          if (navigator.onLine) {
            await updateDoc(ref, updated);
            await dexie.tunes.update(id, updated);
          } else {
            await dexie.tunes.update(id, updated);
            await dexie.pendingWrites.put({
              id,
              type: 'tune',
              operation: 'update',
              data: { id, ...updated }
            });
          }
        }
        setSelectedTuneIds([]);
      } catch (err) {
        console.error('Error updating statuses:', err);
        showNotice('Failed to update statuses.', 'error');
      }
    }
  );
};


const modeTunes = currentMode === 'all' ? tunes : tunes.filter(t => (t.mode || 'tunes') === currentMode);
const genreOptionSet = new Set();
const typeOptionSet = new Set();

[...(labels?.defaultGenres || []), ...modeGenres].forEach((g) => {
  const normalized = normalizeGenre(g);
  if (normalized) genreOptionSet.add(normalized);
});

[...(labels?.defaultTypes || []), ...modeTypes].forEach((t) => {
  const normalized = normalizeType(t);
  if (normalized) typeOptionSet.add(normalized);
});

modeTunes.forEach((t) => {
  const g = normalizeGenre(t.genre);
  const ty = normalizeType(t.type);
  if (g) genreOptionSet.add(g);
  if (ty) typeOptionSet.add(ty);
});

const genreOptions = [...genreOptionSet].sort();
const typeOptions = [...typeOptionSet].sort();
const ratingOptions = [...new Set(tunes.map(t => t.rating).filter(Boolean))].sort();           // ★, ★★, etc.

const getLastNameSortKey = (name = '') => {
  const normalized = String(name).trim().toLowerCase();
  if (!normalized) return 'zzz';
  const parts = normalized.split(/\s+/).filter(Boolean);
  const last = parts[parts.length - 1] || normalized;
  return `${last} ${normalized}`;
};

const sentByOptions = React.useMemo(() => {
  const map = new Map();
  tunes.forEach(t => {
    if (t.importedFrom?.uid) {
      const e = map.get(t.importedFrom.uid);
      if (e) e.count++;
      else map.set(t.importedFrom.uid, { uid: t.importedFrom.uid, name: t.importedFrom.displayName || 'Unknown', count: 1 });
    }
  });
  return Array.from(map.values()).sort((a, b) => getLastNameSortKey(a.name).localeCompare(getLastNameSortKey(b.name)));
}, [tunes]);



const genreCounts = {};
getFilteredSubset("genre").forEach(t => {
  const g = normalizeGenre(t.genre);
  if (g) genreCounts[g] = (genreCounts[g] || 0) + 1;
});


const typeCounts = {};
getFilteredSubset("type").forEach(t => {
  const type = normalizeType(t.type);
  if (type) typeCounts[type] = (typeCounts[type] || 0) + 1;
});

const keyCounts = {};
getFilteredSubset("key").forEach(t => {
  const nk = normalizeKey(t.key);
  if (nk) keyCounts[nk] = (keyCounts[nk] || 0) + 1;
});

const statusCounts = {};
getFilteredSubset("status").forEach(t => {
  const key = t.status || '—';
  statusCounts[key] = (statusCounts[key] || 0) + 1;
});

const ratingCounts = {};
getFilteredSubset("rating").forEach(t => {
  const raw = t.rating;
  const key = (!raw || raw === '—') ? '' : raw;  // normalize all to '' if blank or '—'
  ratingCounts[key] = (ratingCounts[key] || 0) + 1;
});


const labelCounts = {};
getFilteredSubset("ratingLabel").forEach(t => {
  const key = t.ratingLabel || '—';
  labelCounts[key] = (labelCounts[key] || 0) + 1;
});

const modernGenreOptions = [
  { value: '', label: `All ${labels?.genreLabelPlural || 'Genres'}` },
  ...genreOptions.map((g) => ({ value: g, label: `${g} (${genreCounts[g] || 0})` }))
];

const modernTypeOptions = [
  { value: '', label: 'All Types' },
  ...typeOptions.map((t) => ({ value: t, label: `${t.charAt(0).toUpperCase() + t.slice(1)} (${typeCounts[t] || 0})` }))
];

const modernStatusOptions = [
  { value: '', label: 'All Statuses' },
  { value: '—', label: `No Status (${statusCounts['—'] || 0})` },
  ...(labels?.statusOptions || ['Need to Learn', 'Learning', 'Learned'])
    .filter((_, i) => i > 0)
    .map((s) => ({ value: s, label: `${s} (${statusCounts[s] || 0})` }))
];

const modernRatingOptions = [
  { value: '', label: 'All Ratings' },
  ...ratingOptions
    .filter((r) => r && r !== '—')
    .map((r) => ({ value: r, label: `${r} (${ratingCounts[r] || 0})` }))
];

const modernSentByOptions = [
  { value: '', label: 'All Sources' },
  ...sentByOptions.map((o) => ({ value: o.uid, label: `${o.name} (${o.count})` }))
];

const modernSortOptions = [
  { value: 'newestAdded', label: 'Newest' },
  { value: 'oldestAdded', label: 'Oldest' },
  ...(hasImportedNotes && (currentMode === 'notes' || currentMode === 'all')
    ? [
        { value: 'newestImported', label: 'Newest Imported' },
      ]
    : []),
  { value: 'alpha', label: 'A-Z' },
  { value: 'alphaDesc', label: 'Z-A' },
];

const modernBulkActionOptions = [
  {
    label: 'Bulk Actions',
    options: [
      { value: 'action:delete', label: '🗑 Delete Selected' },
      { value: 'action:addset', label: `📁 Create ${labels?.Collection || 'Set'} from Selected` },
      { value: 'action:addtoset', label: `📚 Add to ${labels?.Collection || 'Set'}` },
      { value: 'action:addtags', label: '🏷 Add Tags' },
      { value: 'action:send', label: '✉ Send to Contact' },
      { value: 'action:export', label: '📥 Export Selected' },
    ],
  },
  {
    label: 'Status',
    options: [
      ...(labels?.statusOptions || [])
        .filter((_, i) => i > 0)
        .map((s) => ({ value: `status:${s}`, label: `Mark as ${s}` })),
      { value: 'status:', label: 'Remove Status' },
    ],
  },
  {
    label: 'Rating',
    options: [
      { value: 'rating:★', label: '★' },
      { value: 'rating:★★', label: '★★' },
      { value: 'rating:★★★', label: '★★★' },
      { value: 'rating:★★★★', label: '★★★★' },
      { value: 'rating:★★★★★', label: '★★★★★' },
      { value: 'rating:', label: 'Remove Rating' },
    ],
  },
];

const modernKeyOptions = [
  { value: '', label: 'All Keys' },
  ...Object.keys(keyGroups).sort().map((root) => ({
    label: root,
    options: [
      { value: `root:${root}`, label: `${root} (All Modes)` },
      ...[...keyGroups[root]]
        .sort((a, b) => {
          const rank = (k) => (k.endsWith(' mix') ? 3 : k.endsWith(' dorian') ? 2 : k.endsWith('m') ? 1 : 0);
          return rank(a) - rank(b) || a.localeCompare(b);
        })
        .map((k) => {
          const label = k.endsWith('m')
            ? `${root} minor`
            : k.endsWith(' dorian')
              ? `${root} dorian`
              : k.endsWith(' mix')
                ? `${root} mixolydian`
                : `${root} major`;
          return { value: k, label: `${label} (${keyCounts[k] || 0})` };
        })
    ]
  }))
];


const saveCurrentFilters = async () => {
  if (!user) return;

  const filtersToSave = {
    searchQuery,
    filterKey,
    filterType,
    filterGenre,
    filterStatus,
    filterRating,
    filterRatingLabel,
    filterTimeRange,
    filterTimeBasis,
    filterTimeMonth,
    filterTimeYear,
    selectedTags,
    tagLogic
  };

  try {
    const localBundle = readLocalSearchFilterBundle();
    const nextByMode = {
      ...(localBundle.byMode || {}),
      [normalizeModeKey(currentMode)]: filtersToSave
    };
    const nextLastPureMode = currentMode === 'all'
      ? normalizeModeKey(localBundle.lastPureMode || 'tunes')
      : normalizeModeKey(currentMode);

    localStorage.setItem(
      SEARCH_FILTERS_STORAGE_KEY,
      JSON.stringify({ byMode: nextByMode, lastPureMode: nextLastPureMode })
    );
    localStorage.setItem(LEGACY_SEARCH_FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));

    const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
    await setDoc(
      ref,
      {
        searchFiltersByMode: nextByMode,
        searchFiltersLastPureMode: nextLastPureMode,
        searchFilters: filtersToSave
      },
      { merge: true }
    );
  } catch (err) {
    console.error('Failed to save filters to Firebase:', err);
  }
};

const handleBulkTagConfirm = async () => {
  if (!user || selectedTuneIds.length === 0 || bulkTags.length === 0) return;

  requestActionConfirm(
    {
      title: 'Apply Tags',
      message: `Add tags to ${selectedTuneIds.length} selected ${(labels?.items || 'tunes')}?`,
      confirmLabel: 'Apply',
      cancelLabel: 'Cancel',
    },
    async () => {
      try {
        for (const id of selectedTuneIds) {
          const ref = doc(db, 'users', user.uid, 'tunes', id);

          let currentData;
          if (navigator.onLine) {
            const snap = await getDoc(ref);
            if (!snap.exists()) continue;
            currentData = snap.data();
          } else {
            currentData = await dexie.tunes.get(id);
            if (!currentData) continue;
          }

          const existing = currentData.customTags || [];
          const merged = Array.from(new Set([...existing, ...bulkTags]));
          const updated = { ...currentData, customTags: merged };

          if (navigator.onLine) {
            await updateDoc(ref, { customTags: merged });
            await dexie.tunes.put({ id, ...updated });
          } else {
            await dexie.tunes.put({ id, ...updated });
            await dexie.pendingWrites.put({
              id,
              type: 'tune',
              operation: 'update',
              data: { id, ...updated },
            });
          }
        }

        setShowBulkTagInput(false);
        setBulkTags([]);
        setSelectedTuneIds([]);
        showNotice(`Tags added to ${selectedTuneIds.length} ${(labels?.items || 'tunes')}.`);
      } catch (err) {
        console.error('Failed to update tags:', err);
        showNotice('Failed to apply tags.', 'error');
      }
    }
  );
};

const openBulkAddToSetPicker = async () => {
  if (!user || selectedTuneIds.length === 0) return;
  setBulkAddLoading(true);
  try {
    let sourceSets = [];
    if (navigator.onLine) {
      const snap = await getDocs(collection(db, 'users', user.uid, 'sets'));
      sourceSets = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } else {
      sourceSets = await dexie.sets.toArray();
    }
    const selectedMode = tunes.find((t) => t.id === selectedTuneIds[0])?.mode || (currentMode === 'all' ? 'tunes' : currentMode);
    const eligible = sourceSets
      .filter((set) => !set.isProgram)
      .filter((set) => (set.mode || 'tunes') === selectedMode)
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    setBulkAddTargetSets(eligible);
    setShowBulkAddToSetPicker(true);
  } catch (err) {
    console.error('Failed to load target sets:', err);
    showNotice('Could not load sets.', 'error');
  } finally {
    setBulkAddLoading(false);
  }
};

async function handleBulkAddToSet(targetSetId) {
  if (!user || !targetSetId || selectedTuneIds.length === 0) return;
  const selectedItems = selectedTuneIds
    .map((id) => tunes.find((t) => t.id === id))
    .filter(Boolean)
    .map((t) => ({ id: t.id, title: t.title || '', key: t.key || '', type: t.type || '' }));
  if (selectedItems.length === 0) return;

  try {
    let targetData = null;
    if (navigator.onLine) {
      const snap = await getDoc(doc(db, 'users', user.uid, 'sets', targetSetId));
      if (!snap.exists()) throw new Error('Target set not found');
      targetData = snap.data();
    } else {
      targetData = await dexie.sets.get(targetSetId);
      if (!targetData) throw new Error('Target set not found (offline)');
    }

    const existingTunes = Array.isArray(targetData.tunes) ? targetData.tunes : [];
    const existingIds = new Set(existingTunes.filter((e) => e?.type !== 'set').map((e) => e.id));
    const uniqueToAdd = selectedItems.filter((item) => !existingIds.has(item.id));
    if (uniqueToAdd.length === 0) {
      showNotice('All selected items are already in that set.', 'info');
      return;
    }

    const nextTunes = [...existingTunes, ...uniqueToAdd];
    const updatedAt = new Date().toISOString();

    if (navigator.onLine) {
      await updateDoc(doc(db, 'users', user.uid, 'sets', targetSetId), { tunes: nextTunes, updatedAt });
      await dexie.sets.put({ id: targetSetId, ...targetData, tunes: nextTunes, updatedAt });
    } else {
      await dexie.sets.put({ id: targetSetId, ...targetData, tunes: nextTunes, updatedAt });
      await dexie.pendingWrites.put({
        id: `${targetSetId}__bulkAddToSet`,
        type: 'set',
        operation: 'update',
        data: { id: targetSetId, ...targetData, tunes: nextTunes, updatedAt },
      });
    }

    setSelectedTuneIds([]);
    setShowBulkAddToSetPicker(false);
    setBulkAddToSetQuery('');
    showNotice(`Added ${uniqueToAdd.length} ${(labels?.items || 'tunes')} to "${targetData.title || 'Untitled Set'}".`, 'info');
  } catch (err) {
    console.error('Failed to add items to set:', err);
    showNotice('Failed to add to set.', 'error');
  }
}



useEffect(() => {
  if (!user || !filtersLoaded) return;
  return () => {
    saveCurrentFilters(); // async is okay here; fire-and-forget
  };
}, [user, filtersLoaded]);


const goToRandomTune = async () => {
  if (sortedTunes.length === 0) return;
  await saveCurrentFilters();
  const randomTune = sortedTunes[Math.floor(Math.random() * sortedTunes.length)];
  navigate(`/tune/${randomTune.id}`);
};

async function handleBulkSend(recipientUid, contact) {
  const myProfile = await getUserProfile(user.uid);
  const items = selectedTuneIds.map(id => {
    const t = tunes.find(t => t.id === id);
    return { itemType: 'tune', tuneId: id, tuneTitle: t?.title || 'Untitled' };
  });
  await sendBatchToUser(user.uid, myProfile, recipientUid, items);

  // Record sharedWith on each tune
  for (const id of selectedTuneIds) {
    const ref = doc(db, 'users', user.uid, 'tunes', id);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const existing = snap.data().sharedWith || [];
      if (!existing.some(s => s.uid === recipientUid)) {
        await updateDoc(ref, {
          sharedWith: [...existing, {
            uid: recipientUid,
            displayName: contact?.displayName || '',
            username: contact?.username || '',
            sentAt: new Date().toISOString()
          }]
        });
      }
    }
  }
  setSelectedTuneIds([]);
}


useEffect(() => {
  if (!user) return;

  const handleBeforeUnload = () => {
    // Not awaited — that's fine
    saveCurrentFilters();
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    saveCurrentFilters(); // Also catch React Router unmounts
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, [user, searchQuery, filterKey, filterType, filterGenre, filterStatus, filterRating, filterRatingLabel, filterTimeRange, filterTimeBasis, filterTimeMonth, filterTimeYear, selectedTags]);

useEffect(() => {
  if (!dexieReady || !user) return;

  let unsub = null;

  const loadTunes = async () => {
    if (navigator.onLine) {
      const ref = collection(db, 'users', user.uid, 'tunes');
      unsub = onSnapshot(ref, snapshot => {
        const liveTunes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setTunes(liveTunes);
        dexie.tunes.bulkPut(liveTunes);
      });
    } else {
      const offlineTunes = await dexie.tunes.toArray();
      setTunes(offlineTunes);
    }
  };

  loadTunes();

  return () => {
    if (unsub) unsub();
  };
}, [user, dexieReady]);


if (!user) return <Login />;

if (!dexieReady) {
  return (
    <PageWrapper>
      <p style={{ padding: '2rem' }}>Loading offline data…</p>
    </PageWrapper>
  );
}


  return (
    <PageWrapper>
      {notice && (
        <div style={{
          margin: '0.5rem auto 0.75rem',
          maxWidth: '680px',
          padding: '8px 12px',
          borderRadius: '8px',
          textAlign: 'center',
          backgroundColor: notice.tone === 'error' ? '#5a1f1f' : '#1f3f5a',
          color: '#fff',
          fontSize: '0.9rem'
        }}>
          {notice.text}
        </div>
      )}

      
      {!user ? (
        <Login />
      ) : (
        <>

  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>


<BorderedBox
  style={{ marginBottom: 'var(--ui-section-gap)', paddingTop: 'var(--ui-panel-padding-top)' }}
  collapsible
  scrollWhenExpanded
  expandedMaxHeight="50vh"
  exposeCollapsed={() => setTimeout(updateScrollHeight, 200)}
>



      
  <div className="app-title ui-density-title" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
    <img src={myIcon} alt="Logo" className="responsive-icon" />
    <span>{labels?.navSearch || 'AllMyTunes'}</span>
    <FaQuestionCircle
      onClick={() => navigate(`/help#${currentMode === 'notes' ? 'notes-search' : 'search-filters'}`)}
      style={{ fontSize: '0.65em', cursor: 'pointer', color: 'var(--text-link, #228be6)', opacity: 0.8 }}
      title="Help & Tutorials"
    />
  </div>
  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
    <ModeSwitcher inline />
  </div>

<div style={{ maxWidth: '600px', margin: '0 auto' }}>

<div
  className="ui-density-grid search-filter-grid"
  style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    maxWidth: '750px',
    alignItems: 'center',
  }}
>


  <input
    className="search-input"
    placeholder={searchPlaceholder}
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    style={{
      gridColumn: '1 / -1'
    }}
  />

  <div
    style={{
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns:
        currentMode === 'notes'
          ? 'repeat(2, minmax(0, 1fr))'
          : 'repeat(3, minmax(0, 1fr))',
      gap: 'var(--ui-filter-grid-gap)',
      alignItems: 'center'
    }}
  >
    <AppSelect
      className="search-core-select"
      value={filterGenre || ''}
      onChange={setFilterGenre}
      options={modernGenreOptions}
      placeholder={`All ${labels?.genreLabelPlural || 'Genres'}`}
      active={Boolean(filterGenre)}
    />

    <AppSelect
      className="search-core-select"
      value={filterType || ''}
      onChange={setFilterType}
      options={modernTypeOptions}
      placeholder="All Types"
      active={Boolean(filterType)}
    />

    {!labels?.hideKeyFilter && (
      <AppSelect
        className="search-core-select"
        value={filterKey || ''}
        onChange={setFilterKey}
        options={modernKeyOptions}
        placeholder="All Keys"
        active={Boolean(filterKey)}
      />
    )}
  </div>

  <div
    className="quick-filter-actions search-utility-row"
    style={{
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: currentMode === 'notes' ? 'repeat(4, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
      gap: 'var(--ui-filter-grid-gap)',
      alignItems: 'center'
    }}
  >
    <button
      className={`btn-subtle search-control-btn search-more-btn ${hasAdvancedFiltersActive ? 'search-control-active' : ''} ${showAdvancedFilters ? 'control-ready-glow' : ''}`}
      style={isSimpleMode ? { opacity: 0.5 } : undefined}
      onClick={() => {
        if (isSimpleMode) { setShowAdvancedLockPopup(true); return; }
        setShowAdvancedFilters((prev) => {
          const next = !prev;
          if (!next) {
            setShowTagFilters(false);
            setShowTimeFilters(false);
          }
          return next;
        });
      }}
    >
      {isSimpleMode
        ? (isMobileFilterLayout ? '↓ More 🔒' : '↓ More Filters 🔒')
        : showAdvancedFilters
          ? (isMobileFilterLayout ? '↑ Hide' : '↑ Hide Advanced')
          : (isMobileFilterLayout
            ? `↓ More${hasAdvancedFiltersActive ? ' • Active' : ''}`
            : `↓ More Filters${hasAdvancedFiltersActive ? ' • Active' : ''}`)}
    </button>

    {showTSImportModal && (
      <TheSessionImportModal onClose={() => setShowTSImportModal(false)} />
    )}

    <ActionConfirmModal
      open={showAdvancedLockPopup}
      title="Advanced Filters"
      message="Advanced filters let you narrow results by status, rating, tags, time range, and more. Full Mode also unlocks Sessions (playing history), Social (follow musicians, share tunes), and program templates."
      confirmLabel="Enable Full Mode"
      cancelLabel="Not yet"
      onConfirm={() => { setShowAdvancedLockPopup(false); unlockFullMode(); }}
      onCancel={() => setShowAdvancedLockPopup(false)}
    />

    <button
      className="btn-subtle btn-clear-filters search-control-btn search-clear-btn"
      onClick={clearAllFilters}
      disabled={!hasActiveSearchFilters}
    >
      {isMobileFilterLayout ? 'Clear' : '✕ Clear Filters'}
    </button>

    <button
      className="btn-subtle search-control-btn search-random-btn"
      onClick={goToRandomTune}
    >
      🎲 {isMobileFilterLayout ? 'Random' : (labels?.randomButton || (currentMode === 'notes' ? 'Random Note' : 'Random Tune'))}
    </button>

    {currentMode === 'notes' && (
      <button
        className="btn-subtle search-control-btn"
        onClick={() => setShowQuickAddNote(true)}
        style={{ fontWeight: 600 }}
      >
        + {isMobileFilterLayout ? 'Note' : 'Quick Note'}
      </button>
    )}
  </div>

{showAdvancedFilters && !isSimpleMode && (
  <>
    <AppSelect
      className="search-advanced-select"
      value={filterStatus || ''}
      onChange={setFilterStatus}
      options={modernStatusOptions}
      placeholder="All Statuses"
      active={Boolean(filterStatus)}
    />

    <AppSelect
      className="search-advanced-select"
      value={filterRating || ''}
      onChange={setFilterRating}
      options={modernRatingOptions}
      placeholder="All Ratings"
      active={Boolean(filterRating)}
    />

    <AppSelect
      className="search-advanced-select"
      value={filterSentBy || ''}
      onChange={setFilterSentBy}
      options={modernSentByOptions}
      placeholder="All Sources"
      active={Boolean(filterSentBy)}
      disabled={sentByOptions.length === 0}
    />

    <div
      className="quick-filter-actions search-advanced-row"
      style={{
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 'var(--ui-filter-grid-gap)',
        alignItems: 'center'
      }}
    >
      <button
        className={`btn-subtle search-control-btn search-advanced-btn search-time-btn ${hasActiveTimeFilter ? 'search-control-active' : ''} ${showTimeFilters ? 'panel-open' : ''}`}
        onClick={() => setShowTimeFilters(v => !v)}
      >
        {showTimeFilters
          ? `🕒 ${isMobileFilterLayout ? 'Hide' : 'Hide Time'}`
          : `🕒 ${isMobileFilterLayout ? compactTimeSummaryLabel : timeSummaryLabel}`}
      </button>

      <button
        className={`btn-subtle search-control-btn search-advanced-btn ${selectedTags.length > 0 ? 'search-control-active' : ''} ${showTagFilters ? 'panel-open' : ''}`}
        onClick={() => setShowTagFilters(p => !p)}
      >
        {showTagFilters
          ? `🏷 ${isMobileFilterLayout ? 'Hide' : 'Hide Tags'}${selectedTags.length > 0 ? ` (${selectedTags.length})` : ''}`
          : `🏷 Tags${selectedTags.length > 0 ? ` (${selectedTags.length})` : ''}`}
      </button>

    </div>
  </>
)}

{showTagFilters && (
  <div style={{ gridColumn: '1 / -1' }}>
    <TagFilterControl
      availableTags={availableFilterTags}
      selectedTags={selectedTags}
      setSelectedTags={setSelectedTags}
      tagLogic={tagLogic}
      setTagLogic={setTagLogic}
    />
  </div>
)}

{showTimeFilters && (
  <TimeFilterPanel
    isMobileFilterLayout={isMobileFilterLayout}
    uiSize={activeUiSize}
    basisOptions={timeBasisOptions}
    selectedBasis={filterTimeBasis}
    onSelectBasis={setFilterTimeBasis}
    rangeOptions={timeRangeOptions}
    selectedRange={filterTimeRange}
    onSelectRange={handleTimeRangeSelect}
    hasActiveTimeFilter={hasActiveTimeFilter}
    onClear={() => {
      setFilterTimeRange('');
      setFilterTimeBasis('updated');
      setFilterTimeMonth('');
      setFilterTimeYear('');
    }}
    monthPickerYear={monthPickerYear}
    setMonthPickerYear={setMonthPickerYear}
    selectedMonth={filterTimeMonth}
    onSelectMonth={setFilterTimeMonth}
    selectedYear={filterTimeYear}
    onSelectYear={setFilterTimeYear}
  />
)}
</div>

</div>

{prefs?.showFieldHints && (
  <div style={{ fontSize: 'var(--ui-compact-font)', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', margin: '0.5rem auto', maxWidth: '500px' }}>
    {helperFilterText}
    <div style={{ fontSize: 'var(--ui-subtle-font)', marginTop: '0.25rem', opacity: 0.7 }}>
      <span onClick={() => navigate('/help')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Toggle hints in Help &amp; Tutorials</span>
    </div>
  </div>
)}

          <div className="search-actions-wrap" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div className="tune-toolbar">
            <div className="tune-toolbar-row search-actions-row">
              <AppSelect
                className="compact-action-select search-sort-select"
                value={sortOrder === 'newest' ? 'newestAdded' : (sortOrder === 'oldest' ? 'oldestAdded' : sortOrder)}
                onChange={setSortOrder}
                options={modernSortOptions}
                placeholder="Sort"
                active={false}
              />

              <button
                className={`btn-subtle compact-action-btn search-control-btn search-selectall-btn ${selectedTuneIds.length > 0 ? 'search-control-active' : ''}`}
                onClick={toggleSelectAll}
              >
                {allVisibleTunesSelected ? 'Deselect All' : 'Select All'}
              </button>

            {/*
                          <button
                style={{ ...inputStyle, backgroundColor: 'darkred', color: 'white' }}
                onClick={deleteSelectedTunes}
                disabled={selectedTuneIds.length === 0}
              >
                Delete Selected ({selectedTuneIds.length})
              </button>  

            */}
        
             <AppSelect
            className={`bulk-actions-select search-bulk-select ${selectedTuneIds.length > 0 ? 'bulk-ready control-ready-glow' : ''}`}
            value={bulkActionValue}
            placeholder={
              selectedTuneIds.length > 0
                ? (useCompactBulkLabel
                  ? `${bulkLabelBase} (${selectedTuneIds.length})`
                  : `${bulkLabelBase} (${selectedTuneIds.length} selected)`)
                : bulkLabelBase
            }
            options={modernBulkActionOptions}
            active={selectedTuneIds.length > 0}
            onChange={async (value) => {
              if (!value || selectedTuneIds.length === 0) return;

              setBulkActionValue(''); // reset immediately

              if (value === 'action:delete') {
                deleteSelectedTunes();
                return;
              }

             if (value === 'action:addset') {
  if (!user) return;

  requestActionConfirm(
    {
      title: `Create ${labels?.Collection || 'Set'}`,
      message: `Create new ${labels?.collection || 'set'} with ${selectedTuneIds.length} selected ${labels?.items || 'tunes'}?`,
      confirmLabel: 'Create',
      cancelLabel: 'Cancel',
    },
    async () => {
      const orderedTunes = selectedTuneIds
        .map(id => tunes.find(t => t.id === id))
        .filter(Boolean)
        .map(t => ({
          id: t.id,
          title: t.title || '',
          key: t.key || '',
          type: t.type || '',
        }));

      const ref = doc(collection(db, 'users', user.uid, 'sets'));
      const createdAt = new Date().toISOString();
      const newSet = {
        id: ref.id,
        title: orderedTunes[0]?.title || `Untitled ${labels?.Collection || 'Set'}`,
        mode: currentMode,
        createdAt,
        tunes: orderedTunes
      };

      if (navigator.onLine) {
        await setDoc(ref, newSet);
        await dexie.sets.put(newSet);
      } else {
        await dexie.sets.put(newSet);
        await dexie.pendingWrites.put({
          id: newSet.id,
          type: 'set',
          operation: 'add',
          data: newSet
        });
      }

      for (const t of orderedTunes) {
        const tuneRef = doc(db, 'users', user.uid, 'tunes', t.id);

        let currentTune;
        if (navigator.onLine) {
          const snap = await getDoc(tuneRef);
          if (!snap.exists()) continue;
          currentTune = snap.data();
        } else {
          currentTune = await dexie.tunes.get(t.id);
        }

        if (!currentTune) continue;

        const existingSets = currentTune.sets || [];
        const newSetEntry = {
          setId: newSet.id,
          title: newSet.title,
          createdAt: newSet.createdAt
        };

        const updatedSets = [...existingSets.filter(s => s.setId !== newSet.id), newSetEntry];
        const updatedTune = { ...currentTune, sets: updatedSets };

        if (navigator.onLine) {
          await updateDoc(tuneRef, { sets: updatedSets });
          await dexie.tunes.put({ id: t.id, ...updatedTune });
        } else {
          await dexie.tunes.put({ id: t.id, ...updatedTune });
          await dexie.pendingWrites.put({
            id: t.id,
            type: 'tune',
            operation: 'update',
            data: { id: t.id, ...updatedTune }
          });
        }
      }

      navigate(`/sets/${newSet.id}`);
    }
  );
  return;
}


              if (value === 'action:addtags') {
                setShowBulkTagInput(true);
                return;
              }

              if (value === 'action:addtoset') {
                await openBulkAddToSetPicker();
                return;
              }

              if (value === 'action:send') {
                setShowBulkSendPicker(true);
                return;
              }

              if (value === 'action:export') {
                setBulkExportTunes(tunes.filter(t => selectedTuneIds.includes(t.id)));
                setShowBulkExportModal(true);
                return;
              }

              const [field, raw] = value.split(':');
              const fieldMap = {
                status: 'status',
                rating: 'rating',
              };
              const updateField = fieldMap[field];
              const newValue = raw || null;

              const confirmText = newValue
                ? `Set ${updateField} to "${newValue}"`
                : `Remove ${updateField}`;

              requestActionConfirm(
                {
                  title: 'Apply Bulk Update',
                  message: `${confirmText} for ${selectedTuneIds.length} selected ${(labels?.items || 'tunes')}?`,
                  confirmLabel: 'Apply',
                  cancelLabel: 'Cancel',
                },
                async () => {
                  for (const id of selectedTuneIds) {
                    const ref = doc(db, 'users', user.uid, 'tunes', id);
                    await updateDoc(ref, { [updateField]: newValue });
                  }

                  setSelectedTuneIds([]);
                }
              );
              return;
            }}
          />
            </div>
          </div>
          </div>

{showBulkTagInput && (
  <div
    style={{
      marginTop: '0.5rem',
      padding: '0.5rem',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
      Add tags to selected {labels?.items || 'tunes'}:
    </p>

 <div
  style={{
    width: '100%',
    maxWidth: '400px',
    boxSizing: 'border-box',
    padding: '0 1rem',
  }}
>
  <CustomTagInput
    tags={bulkTags}
    setTags={setBulkTags}
    suggestions={availableFilterTags}
    inputStyle={inputStyle}
  />
</div>


    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem' }}>
      <button
        onClick={handleBulkTagConfirm}
        style={{
          padding: '6px 12px',
          fontSize: '0.8rem',
          borderRadius: '6px',
          backgroundColor: '#228be6',
          color: 'white'
        }}
      >
        Apply Tags
      </button>

      <button
        onClick={() => {
          setShowBulkTagInput(false);
          setBulkTags([]);
        }}
        style={{ fontSize: '0.8rem' }}
      >
        Cancel
      </button>
    </div>
  </div>
)}

{showBulkAddToSetPicker && (
  <div
    style={{
      marginTop: '0.5rem',
      padding: '0.5rem',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
      Add selected {labels?.items || 'tunes'} to:
    </p>
    <div style={{ width: '100%', maxWidth: '420px', boxSizing: 'border-box', padding: '0 1rem' }}>
      <input
        className="search-input"
        placeholder={`Search ${labels?.collections || 'sets'}...`}
        value={bulkAddToSetQuery}
        onChange={(e) => setBulkAddToSetQuery(e.target.value)}
        style={{ ...inputStyle, width: '100%', marginBottom: '0.5rem' }}
      />
      {bulkAddLoading ? (
        <p style={{ textAlign: 'center', opacity: 0.75 }}>Loading...</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflowY: 'auto' }}>
          {bulkAddTargetSets
            .filter((set) => !bulkAddToSetQuery.trim() || (set.title || '').toLowerCase().includes(bulkAddToSetQuery.toLowerCase()))
            .slice(0, 60)
            .map((set) => (
              <button
                key={set.id}
                className="btn-subtle search-control-btn"
                onClick={() => handleBulkAddToSet(set.id)}
                style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '6px 10px' }}
              >
                {set.title || 'Untitled Set'}
              </button>
            ))}
        </div>
      )}
    </div>
    <div style={{ marginTop: '0.6rem' }}>
      <button
        className="btn-subtle search-control-btn"
        onClick={() => {
          setShowBulkAddToSetPicker(false);
          setBulkAddToSetQuery('');
        }}
      >
        Cancel
      </button>
    </div>
  </div>
)}



</BorderedBox>

          {pendingDelete && (
            <UndoBanner
              className="undo-banner-inline"
              message={`${(() => {
                const deletedTunes = Array.isArray(pendingDelete.tunes) ? pendingDelete.tunes : [];
                const deletedSets = Array.isArray(pendingDelete.sets) ? pendingDelete.sets : [];
                const tuneCount = deletedTunes.length;
                const setCount = deletedSets.length;
                const tuneModes = new Set(
                  deletedTunes.map((entry) => (entry?.data?.mode || 'tunes'))
                );
                const setModes = new Set(
                  deletedSets.map((entry) => (entry?.mode || 'tunes'))
                );
                const hasNotes = tuneModes.has('notes');
                const hasTunes = tuneModes.has('tunes');
                const itemWord = hasNotes && hasTunes
                  ? (tuneCount === 1 ? 'item' : 'items')
                  : hasNotes
                    ? (tuneCount === 1 ? 'note' : 'notes')
                    : (tuneCount === 1 ? 'tune' : 'tunes');
                const fromWord = setModes.size > 1
                  ? (setCount === 1 ? 'collection' : 'collections')
                  : setModes.has('notes')
                    ? (setCount === 1 ? 'folder' : 'folders')
                    : (setCount === 1 ? 'set' : 'sets');
                return `Deleted ${tuneCount} ${itemWord}${setCount > 0 ? ` from ${setCount} ${fromWord}` : ''}.`;
              })()}`}
              onUndo={undoDelete}
            />
          )}

          {filterSentBy && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', marginBottom: '0.5rem', padding: '6px 12px',
              backgroundColor: 'var(--input-bg)', borderRadius: '8px', fontSize: '0.85rem'
            }}>
              <span>
                Showing {currentMode === 'notes' ? 'notes' : currentMode === 'all' ? (labels?.items || 'items') : 'tunes'} from{' '}
                <strong>{sentByOptions.find(o => o.uid === filterSentBy)?.name || 'contact'}</strong>
              </span>
              <button className="btn-subtle" onClick={() => { setFilterSentBy(''); navigate('/search'); }} style={{ padding: '2px 8px', fontSize: '0.8rem' }}>
                Clear
              </button>
            </div>
          )}

          {WhatsNewBanner && <WhatsNewBanner />}
          {NudgeBanner && <NudgeBanner />}

          {filteredTunes.length === 0 ? (
            tunes.length === 0 ? (
              // Truly empty library
              <div style={{ textAlign: 'center', padding: '2rem 1rem', lineHeight: '1.8' }}>
                <p style={{ fontSize: '1.2em', marginBottom: '0.4rem' }}>Your library is empty.</p>
                {labels?.items === 'notes' ? (
                  <>
                    <p style={{ opacity: 0.8, marginBottom: '1.2rem' }}>Capture ideas, links, references, tasks, and more.</p>
                    <button className="btn-subtle" onClick={() => navigate('/add')}>+ Add a Note</button>
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: '0.9em', opacity: 0.8, marginBottom: '1.2rem' }}>
                      Go to the <button className="btn-subtle" style={{ display: 'inline', padding: '2px 8px' }} onClick={() => navigate('/add')}>Add page</button> to add a tune or use the import tools — or bulk-import a TheSession.org tunebook here:
                    </p>
                    <button className="btn-subtle" onClick={() => setShowTSImportModal(true)}>Bulk import from thesession.org</button>
                  </>
                )}
              </div>
            ) : !hasActiveSearchFilters ? (
              // Has entries but none in this mode — not a filter problem
              <div style={{ textAlign: 'center', padding: '1.5rem 1rem', lineHeight: '1.8' }}>
                <p style={{ fontSize: '1.05em', marginBottom: '0.4rem' }}>No {labels?.items || 'tunes'} here yet.</p>
                <p style={{ fontSize: '0.9em', opacity: 0.8, marginBottom: '1rem' }}>
                  Add your first {labels?.item || 'tune'} or import a collection to get started.
                </p>
                <p style={{ fontSize: '0.9em', opacity: 0.8, marginBottom: '1rem' }}>
                  Go to the <button className="btn-subtle" style={{ display: 'inline', padding: '2px 8px' }} onClick={() => navigate('/add')}>Add page</button> to add a {labels?.item || 'tune'} or use the import tools{currentMode !== 'notes' ? ' — or bulk-import a TheSession.org tunebook here:' : '.'}
                </p>
                {currentMode !== 'notes' && (
                  <button className="btn-subtle" onClick={() => setShowTSImportModal(true)}>Bulk import from thesession.org</button>
                )}
              </div>
            ) : (
              // Has entries but active filters are hiding them
              <div style={{ textAlign: 'center', padding: '1.5rem 1rem', lineHeight: '1.8' }}>
                <p style={{ fontSize: '1.05em', marginBottom: '0.35rem' }}>No {labels?.items || 'tunes'} match your filters.</p>
                <p style={{ fontSize: '0.9em', opacity: 0.8, marginBottom: '0.75rem' }}>
                  Try clearing one filter or switch the sort/filter combination.
                </p>
                <button className="btn-subtle btn-clear-filters" onClick={clearAllFilters}>{isMobileFilterLayout ? 'Clear Filters' : '✕ Clear Filters'}</button>
              </div>
            )
          ) : (
<div
className="scroll-container"
  ref={resultsContainerRef}
  style={{
    overflowY: 'auto',
    paddingRight: '0px',
    paddingLeft: '0px',
    marginBottom: '2rem',
    boxSizing: 'border-box',
    height: scrollAreaHeight,
  }}
>



              <ul className="card-list">
                {sortedTunes.filter(t => !pendingDelete?.tunes?.some(p => p.id === t.id)).map((tune) => {
                  const itemMode = tune.mode || 'tunes';
                  const isNoteItem = itemMode === 'notes';
                  const useStackedPreviewLayout = isNarrowCardLayout;
                  const showNotePreview = Boolean(prefs?.showNotePreview);
                  const showTagPreview = prefs?.showTagPreviewInSearch !== false;
                  const showTuneCreatedDate = prefs?.showTuneCreatedDateInSearch !== false;
                  const showNoteCreatedDate = prefs?.showNoteCreatedDateInSearch !== false;
                  const notePreviewText = getNotePreviewText(tune.notes || '');
                  const createdAtDate = toDateValue(tune.createdAt);
                  const importedOnDate = toDateValue(tune.importedOn);
                  const hasPreviewContent = showTagPreview || (!isNoteItem && prefs?.showAbcInSearch && tune.abc);
                  const normalizedSearchTagQuery = normalizeTag((searchQuery || '').trim());
                  const previewBlock = hasPreviewContent ? (
                    <div
                      style={{
                        flex: useStackedPreviewLayout ? '1 1 auto' : '0 0 auto',
                        width: useStackedPreviewLayout ? '100%' : 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: useStackedPreviewLayout ? 'flex-start' : 'flex-end',
                        gap: '4px',
                        maxWidth: useStackedPreviewLayout
                          ? '100%'
                          : ((!isNoteItem && prefs?.showAbcInSearch && tune.abc) ? '320px' : '200px'),
                        marginTop: useStackedPreviewLayout ? '4px' : 0
                      }}
                    >
                      {showTagPreview && Array.isArray(tune.customTags) && tune.customTags.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: useStackedPreviewLayout ? 'flex-start' : 'flex-end', gap: '4px' }}>
                          {tune.customTags.slice(0, 6).map((tag, idx) => {
                            const normalizedTag = normalizeTag(tag || '');
                            const active = normalizeTagsArray(selectedTags).includes(normalizedTag) || (
                              Boolean(normalizedSearchTagQuery) && normalizedTag.includes(normalizedSearchTagQuery)
                            );
                            return (
                              <button
                                key={`${tune.id}-tag-${idx}`}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addTagFromPreview(tag);
                                }}
                                className="btn-subtle"
                                style={{
                                  fontSize: 'var(--ui-card-chip-font)',
                                  padding: '1px 6px',
                                  borderRadius: '10px',
                                  lineHeight: 1.15,
                                  minHeight: '20px',
                                  whiteSpace: 'nowrap',
                                  textDecoration: 'underline',
                                  backgroundColor: active
                                    ? 'color-mix(in srgb, var(--text-link, #228be6) 22%, transparent)'
                                    : 'color-mix(in srgb, var(--text-muted) 16%, transparent)',
                                  border: active
                                    ? '1px solid color-mix(in srgb, var(--text-link, #228be6) 58%, transparent)'
                                    : '1px solid transparent',
                                  color: 'var(--text-primary)'
                                }}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {!isNoteItem && prefs?.showAbcInSearch && tune.abc && (
                        <div style={{ width: useStackedPreviewLayout ? '100%' : 'min(320px, 100%)', maxWidth: '320px', overflow: 'hidden', height: '50px' }}>
                          <AbcPreview abc={tune.abc} width={320} scale={0.55} />
                        </div>
                      )}
                    </div>
                  ) : null;
                  return (
<SwipeablePinnableRow
  key={tune.id}
  pinned={tune.pinned}
  actions={[
    { icon: '📤', label: 'Send / Export', color: '#1c7ed6', onAction: () => setSwipeSendTarget(tune) },
    { icon: '🗑', label: 'Delete', color: '#e03131', onAction: () => swipeDeleteTune(tune) },
    { icon: tune.pinned ? '📌' : '📍', label: tune.pinned ? 'Unpin' : 'Pin', color: '#e07622', onAction: () => swipePinTune(tune) },
  ]}
>
<TuneCardBox
  itemMode={itemMode}
  onClick={async () => {
    try {
      const ref = doc(db, 'users', user.uid, 'tunes', tune.id);
      const snap = await getDoc(ref);
      const fullTune = snap.exists() ? snap.data() : null;

      if (activeSession && user && fullTune) {
        const sessions = fullTune.sessions || [];
        const alreadyIn = sessions.some(s => s.sessionId === activeSession.id);

    if (!alreadyIn) {
  setPendingSessionTune({ id: tune.id, title: tune.title });
  return;
}

      }

      const activeFilters = {
        searchQuery,
        filterKey,
        filterType,
        filterGenre,
        filterStatus,
        filterRating,
        filterRatingLabel,
        selectedTags
      };

      navigate(`/tune/${tune.id}`, {
        state: {
          searchTuneIds: sortedTunes.map(t => t.id),
          currentIndex: sortedTunes.findIndex(t => t.id === tune.id),
          activeFilters
        }
      });

    } catch (err) {
      console.error('Error tagging tune with session:', err);
      navigate(`/tune/${tune.id}`, {
        state: {
          searchTuneIds: sortedTunes.map(t => t.id),
          currentIndex: sortedTunes.findIndex(t => t.id === tune.id)
        }
      });
    }
  }}
>
  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--ui-filter-grid-gap)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ui-filter-grid-gap)', flex: '1 1 200px', minWidth: '160px' }}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          toggleTuneSelection(tune.id);
        }}
        style={{
          padding: '6px',
          margin: '-6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '40px',
          minHeight: '40px',
          touchAction: 'manipulation',
        }}
      >
        <StyledCheckbox
          checked={selectedTuneIds.includes(tune.id)}
          onChange={() => {}}
        />
      </div>

      <div>
        {isNoteItem ? (
          <div className="preview-title-row">
            <strong style={{ fontSize: 'var(--ui-card-title-font)' }}>{tune.title}</strong>
            {currentMode === 'all' && (
              <PreviewModePill variant="note">NOTE</PreviewModePill>
            )}
            <span style={{ fontSize: 'var(--ui-card-meta-font)', color: 'var(--text-secondary)' }}>
              {' - '}
              {[
                showNoteCreatedDate && createdAtDate
                  ? formatShortUsDate(createdAtDate)
                  : null,
                importedOnDate ? `Imported on ${formatShortUsDate(importedOnDate)}` : null,
                [normalizeGenre(tune.genre), normalizeType(tune.type)].filter(Boolean).join(' / ')
              ].filter(Boolean).join(' - ')}
            </span>
          </div>
        ) : (
          <>
            <div className="preview-title-row">
              <strong style={{ fontSize: 'var(--ui-card-title-font)' }}>{tune.title}</strong>
              {currentMode === 'all' && (
                <PreviewModePill variant="tune">TUNE</PreviewModePill>
              )}
              <span style={{ fontSize: 'var(--ui-card-meta-font)', color: 'var(--text-secondary)' }}>
                {' - '}
                {[
                  showTuneCreatedDate && createdAtDate ? formatShortUsDate(createdAtDate) : null,
                  [normalizeGenre(tune.genre), normalizeType(tune.type), normalizeKey(tune.key)].filter(Boolean).join(' / ')
                ].filter(Boolean).join(' - ')}
              </span>
            </div>
          </>
        )}

        {(() => {
          const q = (searchQuery || '').trim();
          if (!q) return null;
          const tokens = getQueryTokens(q);
          const matches = searchMatchMap.get(tune.id) || [];
          if (!matches.length) return null;

          const selectedMatches = [];
          const coveredTokens = new Set();
          for (const match of matches) {
            const addsNewToken = (match.matchedTokens || []).some(token => !coveredTokens.has(token));
            if (selectedMatches.length === 0 || addsNewToken) {
              selectedMatches.push(match);
              (match.matchedTokens || []).forEach(token => coveredTokens.add(token));
            }
            if (coveredTokens.size >= tokens.length || selectedMatches.length >= 2) break;
          }

          return (
            <div style={{ marginTop: '4px', fontSize: 'var(--ui-card-sub-font)', color: 'var(--text-muted)', lineHeight: 1.25 }}>
              {selectedMatches.map((match, idx) => {
                const snippet = buildSnippetForMatch(match.value, match.matchedTokens || tokens);
                return (
                  <div key={`${match.label}-${idx}`}>
                    <strong>{match.label}:</strong> {snippet.prefix}{renderHighlighted(snippet.text, q)}{snippet.suffix}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {!isNoteItem && tune.quickNote && (
          <div
            style={{
              marginTop: '4px',
              fontSize: 'var(--ui-card-sub-font)',
              color: '#888',
              fontStyle: 'italic',
            }}
          >
            {tune.quickNote}
          </div>
        )}

        {isNoteItem && showNotePreview && notePreviewText && (
          <div
            style={{
              marginTop: '4px',
              fontSize: 'var(--ui-card-sub-font)',
              color: 'var(--text-muted)',
              lineHeight: '1.35em',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {notePreviewText}
          </div>
        )}

        {!isNoteItem && prefs?.showLyricsInSearch && tune.lyrics && (
          <div
            style={{
              marginTop: '4px',
              fontSize: 'var(--ui-card-sub-font)',
              color: '#888',
              lineHeight: '1.2em',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
            }}
          >
            {tune.lyrics.split('\n').slice(0, 2).join('\n')}
          </div>
        )}

        {useStackedPreviewLayout && previewBlock}

      </div>
    </div>

    {!useStackedPreviewLayout && previewBlock}
  </div>

  {sessionConfirmations[tune.id] && (
    <div
      className="confirmation-message"
      style={{
        backgroundColor: '#003366',
        color: 'white',
        padding: '0.5rem 1rem',
        marginTop: '0.5rem',
        borderRadius: '6px',
        textAlign: 'center',
        cursor: 'pointer',
        fontSize: '0.9rem'
      }}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          const ref = doc(db, 'users', user.uid, 'tunes', tune.id);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data();
            const filteredSessions = (data.sessions || []).filter(s => s.sessionId !== activeSession.id);
            await dexie.tunes.put({ id: tune.id, ...data, sessions: filteredSessions });

            if (!navigator.onLine) {
              await dexie.pendingWrites.put({
                id: tune.id,
                type: 'tune',
                operation: 'update',
                data: { id: tune.id, ...data, sessions: filteredSessions }
              });
            }

            setSessionConfirmations(prev => {
              const updated = { ...prev };
              delete updated[tune.id];
              return updated;
            });
          }
        } catch (err) {
          console.error('Error removing session from tune:', err);
          showNotice('Could not remove session tag.', 'error');
        }
      }}
    >
      {(labels?.Item || 'Item')} tagged to active session. <span style={{ opacity: 0.8 }}>Tap to undo.</span>
    </div>
  )}
</TuneCardBox>
</SwipeablePinnableRow>
                  );
                })}

              </ul>
            </div>
          )}
          </div>
        </>
      )}

  {pendingSessionTune && (
  <SessionPromptModal
    message={`Tag "${pendingSessionTune.title}" to the active session?`}
    onConfirm={async () => {
      try {
        const ref = doc(db, 'users', user.uid, 'tunes', pendingSessionTune.id);
        const snap = await getDoc(ref);
        if (!snap.exists()) return;

        const fullTune = snap.data();
        const sessions = fullTune.sessions || [];

        const fullSession = {
          sessionId: activeSession.id || '',
          location: activeSession.location || '',
          startedAt: activeSession.startedAt || new Date().toISOString()
        };

        const updatedSessions = [
          ...sessions.filter(s => s.sessionId !== fullSession.sessionId),
          fullSession
        ];

        await updateDoc(ref, { sessions: updatedSessions });
        await dexie.tunes.put({ id: pendingSessionTune.id, ...fullTune, sessions: updatedSessions });

        if (!navigator.onLine) {
          await dexie.pendingWrites.put({
            type: 'update',
            collection: 'tunes',
            docId: pendingSessionTune.id,
            data: { id: pendingSessionTune.id, ...fullTune, sessions: updatedSessions }
          });
        }

        setSessionConfirmations(prev => ({
          ...prev,
          [pendingSessionTune.id]: true
        }));

        setTimeout(() => {
          setSessionConfirmations(prev => {
            const updated = { ...prev };
            delete updated[pendingSessionTune.id];
            return updated;
          });
        }, 5000);
      } catch (err) {
        console.error('Failed to add tune to session', err);
      } finally {
        setPendingSessionTune(null);
      }
    }}
    onCancel={() => {
      navigate(`/tune/${pendingSessionTune.id}`);
      setPendingSessionTune(null);
    }}
  />
)}

<ActionConfirmModal
  open={showDeleteSelectedModal}
  title={`Delete ${selectedTuneIds.length === 1 ? selectedDeleteItemLabel.singular : selectedDeleteItemLabel.plural}`}
  message={`Delete ${selectedTuneIds.length} selected ${selectedTuneIds.length === 1
    ? selectedDeleteItemLabel.singular
    : selectedDeleteItemLabel.plural}?`}
  confirmLabel="Delete"
  cancelLabel="Cancel"
  danger
  onConfirm={async () => {
    await executeDeleteSelectedTunes();
    setShowDeleteSelectedModal(false);
  }}
  onCancel={() => setShowDeleteSelectedModal(false)}
/>

<ActionConfirmModal
  open={actionConfirm.open}
  title={actionConfirm.title}
  message={actionConfirm.message}
  confirmLabel={actionConfirm.confirmLabel}
  cancelLabel={actionConfirm.cancelLabel}
  danger={actionConfirm.danger}
  onConfirm={handleActionConfirm}
  onCancel={cancelActionConfirm}
/>

      <ExportModal
        open={showBulkExportModal}
        onClose={() => setShowBulkExportModal(false)}
        type="bulk"
        item={null}
        tunes={bulkExportTunes}
      />

      <QuickAddNoteModal
        open={showQuickAddNote}
        onClose={() => setShowQuickAddNote(false)}
        user={user}
        genreOptions={genreOptions}
        typeOptions={typeOptions}
        tagOptions={availableFilterTags}
      />

      <SendExportModal
        open={!!swipeSendTarget}
        onClose={() => setSwipeSendTarget(null)}
        title={swipeSendTarget?.title}
        user={user}
        onSend={handleSwipeSendTune}
        type="tune"
        item={swipeSendTarget}
        tunes={[]}
      />

      <SendExportModal
        open={showBulkSendPicker}
        onClose={() => setShowBulkSendPicker(false)}
        title={`${selectedTuneIds.length} tune${selectedTuneIds.length !== 1 ? 's' : ''}`}
        user={user}
        onSend={handleBulkSend}
        type="bulk"
        item={null}
        tunes={tunes.filter(t => selectedTuneIds.includes(t.id))}
        defaultTab="send"
      />

    </PageWrapper>
  );
}
